/**
 * Единственный источник истины по функциональным зонам.
 *
 * Из этого файла собирается и легенда, и выражение `fill-color` для MapLibre.
 * Дублировать палитру где-либо ещё нельзя: разъехавшиеся цвета карты и легенды
 * на градостроительной документации — это не косметический баг, а неверно
 * прочитанный документ.
 *
 * Колористика следует сложившейся практике оформления генпланов: жилые зоны —
 * жёлто-оранжевые, общественно-деловые — красные, производственные —
 * фиолетово-серые, рекреационные — зелёные, инфраструктура — серая.
 */

import type { ExpressionSpecification } from "@maplibre/maplibre-gl-style-spec";

export interface ZoneDefinition {
  /** Код зоны ровно в том виде, в каком он лежит в атрибутах тайла. */
  code: string;
  title: string;
  color: string;
  description?: string;
}

export interface ZoneGroup {
  id: string;
  title: string;
  zones: ZoneDefinition[];
}

/**
 * Имена атрибутов, в которых может лежать код зоны. Порядок — приоритет поиска.
 *
 * Выгрузки ФГИС ТП и муниципальных ГИС называют это поле по-разному, а править
 * конвейер под каждый новый источник дороже, чем перебрать несколько вариантов.
 * Здесь перечислены результаты транслитерации реальных названий: «Код зоны»,
 * «Индекс зоны», «Индекс», «Код», ZONE_CODE, ID_ZONE и так далее.
 *
 * Поля с НАЗВАНИЕМ зоны («Функциональная зона», «Наименование зоны») сюда
 * намеренно не входят: там лежит текст, а не код, и раскраска по нему
 * молча уехала бы в серый fallback для всех объектов сразу.
 */
export const ZONE_CODE_FIELDS = [
  "zone_code",
  "zone_kod",
  "kod_zony",
  "indeks_zony",
  "index_zony",
  "id_zone",
  "kod",
  "indeks",
  "code",
] as const;

export const UNKNOWN_ZONE_COLOR = "#c9ccd1";

export const ZONE_GROUPS: ZoneGroup[] = [
  {
    id: "residential",
    title: "Жилые зоны",
    zones: [
      {
        code: "Ж-1",
        title: "Индивидуальная жилая застройка",
        color: "#fde8c8",
        description: "Индивидуальные жилые дома с приусадебными участками",
      },
      {
        code: "Ж-2",
        title: "Малоэтажная жилая застройка",
        color: "#fccf94",
        description: "Блокированные и малоэтажные многоквартирные дома до 4 этажей",
      },
      {
        code: "Ж-3",
        title: "Среднеэтажная жилая застройка",
        color: "#f9a85c",
        description: "Многоквартирные дома от 5 до 8 этажей",
      },
      {
        code: "Ж-4",
        title: "Многоэтажная жилая застройка",
        color: "#ef7f2f",
        description: "Многоквартирные дома от 9 этажей",
      },
    ],
  },
  {
    id: "public",
    title: "Общественно-деловые зоны",
    zones: [
      { code: "ОД-1", title: "Многофункциональная общественно-деловая зона", color: "#e05252" },
      { code: "ОД-2", title: "Зона специализированной общественной застройки", color: "#c23b3b" },
      { code: "ОД-3", title: "Зона исторического центра", color: "#9e2b2b" },
    ],
  },
  {
    id: "industrial",
    title: "Производственные зоны",
    zones: [
      { code: "П-1", title: "Производственная зона", color: "#9b8fc4" },
      { code: "П-2", title: "Коммунально-складская зона", color: "#7d70ad" },
      { code: "П-3", title: "Зона научно-производственной деятельности", color: "#b3a8d6" },
    ],
  },
  {
    id: "infrastructure",
    title: "Инженерная и транспортная инфраструктура",
    zones: [
      { code: "Т", title: "Зона транспортной инфраструктуры", color: "#9aa3ad" },
      { code: "И", title: "Зона инженерной инфраструктуры", color: "#7f8891" },
    ],
  },
  {
    id: "recreation",
    title: "Рекреационные зоны",
    zones: [
      { code: "Р-1", title: "Зона озеленённых территорий общего пользования", color: "#a8d5a2" },
      { code: "Р-2", title: "Зона отдыха и туризма", color: "#7fc17c" },
      { code: "Р-3", title: "Зона природных территорий", color: "#57a457" },
    ],
  },
  {
    id: "agricultural",
    title: "Сельскохозяйственное использование",
    zones: [
      { code: "СХ-1", title: "Зона сельскохозяйственных угодий", color: "#dbe3a6" },
      { code: "СХ-2", title: "Зона садоводства и огородничества", color: "#c5d180" },
    ],
  },
  {
    id: "special",
    title: "Специальное назначение и акватории",
    zones: [
      { code: "СП-1", title: "Зона кладбищ", color: "#8d8477" },
      { code: "СП-2", title: "Зона складирования и захоронения отходов", color: "#6f685e" },
      { code: "В", title: "Зона акваторий", color: "#a8cfe8" },
    ],
  },
];

/** Плоский индекс код → определение зоны. */
export const ZONE_BY_CODE: ReadonlyMap<string, ZoneDefinition> = new Map(
  ZONE_GROUPS.flatMap((group) => group.zones).map((zone) => [zone.code, zone]),
);

/**
 * Выражение, читающее код зоны из первого попавшегося подходящего атрибута.
 * `coalesce` перебирает варианты имён полей по порядку.
 */
export function zoneCodeExpression(): ExpressionSpecification {
  return [
    "coalesce",
    ...ZONE_CODE_FIELDS.map((field) => ["get", field] as ExpressionSpecification),
    "",
  ] as unknown as ExpressionSpecification;
}

/**
 * Выражение `fill-color`: сопоставляет код зоны цвету из палитры выше.
 * Коды, которых нет в палитре, красятся нейтральным серым — так на карте сразу
 * видно, что в исходных данных встретилось незнакомое обозначение.
 */
export function zoneFillColorExpression(): ExpressionSpecification {
  const cases: (string | ExpressionSpecification)[] = [];
  for (const group of ZONE_GROUPS) {
    for (const zone of group.zones) {
      cases.push(zone.code, zone.color);
    }
  }
  return [
    "match",
    zoneCodeExpression(),
    ...cases,
    UNKNOWN_ZONE_COLOR,
  ] as unknown as ExpressionSpecification;
}

/** Код зоны из свойств объекта — та же логика, что в выражении, но для UI. */
export function readZoneCode(properties: Record<string, unknown> | null | undefined): string | null {
  if (!properties) return null;
  for (const field of ZONE_CODE_FIELDS) {
    const value = properties[field];
    if (typeof value === "string" && value.trim() !== "") return value.trim();
    if (typeof value === "number") return String(value);
  }
  return null;
}

export function describeZone(code: string | null): ZoneDefinition | null {
  if (!code) return null;
  return ZONE_BY_CODE.get(code) ?? null;
}
